import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Account, Customer, CustomerAccount, CustomerAccountItem } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as ProfileActions from '@app/modules/profile/profile.actions';
import * as fromProfile from './profile.reducers';
import * as fromAppRoot from '@app/app.reducers';

import { MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { ProfileTab } from '@shared/models/Profile';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false
})
export class ProfileComponent implements OnInit, OnDestroy {
  private readonly onDestroy$ = new Subject<void>();
  customer: Partial<Customer>;
  currentAccountDetail: Account;
  openTab: ProfileTab = 'showPersonal';
  step: number;
  provider: string;
  difference = false;
  spinner = false;
  showPass = { current_pass: false, new_pass: false, repeat_pass: false };
  enabled: boolean;
  loading = false;
  submitted = false;
  mobileView = false;

  hasI4KAccount = false;

  constructor(
    public router: Router,
    private trackingService: TrackingService,
    private store: Store
  ) {}

  ngOnInit(): void {
    if (window.innerWidth < 768) {
      this.mobileView = true;
    } else {
      this.mobileView = false;
    }

    this.store.pipe(select(fromAppRoot.selectCustomerAndCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value || !value.customer || !value.customer.email) {
        return;
      }

      this.customer = value.customer;
      this.currentAccountDetail = value.currentAccountDetail;
      this.enabled = value.customer.otp_module === 'enabled';
      this.step = value.currentAccountDetail.step_status;
      this.provider = value.customer.provider;
      this.hasI4KAccount = this.customer.accounts.some((customerAccount: CustomerAccountItem) => customerAccount.kind === 'kid');

      if (!this.customer.accounts.length || (!this.customer.accounts.length && this.customer.has_crowd_investor_account)) {
        this.openTab = 'showLanguage';
      }
    });

    this.store
      .pipe(
        select(fromProfile.selectIsInitialised),
        filter((value) => value === false),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.store.dispatch(ProfileActions.getPersonalDetails());
        this.store.dispatch(AppRootActions.getPromotions());
      });
  }

  showPromotions(): void {
    this.store.dispatch(AppRootActions.showPromotionsDialog({ show: true }));
    this.saveUserActivity('showPromotions');
  }

  changeTab(event: ProfileTab): void {
    if (window.innerWidth < 768) {
      if (event === this.openTab) {
        this.openTab = '';
        return;
      }
    }

    this.openTab = event;

    this.saveUserActivity(this.openTab);

    this.submitted = false;
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  saveUserActivity(type: string): void {
    let activity = '';
    let actionType = 'Tab';
    // eslint-disable-next-line default-case
    switch (type) {
      case 'showPersonal':
        activity = 'Personal details';
        break;
      case 'showPass':
        activity = 'Change password';
        break;
      case 'show2FA':
        activity = '2FA';
        break;
      case 'showChangeStragy':
        activity = 'Change strategy';
        break;
      case 'showLanguage':
        activity = 'Change language';
        break;
      case 'showPromotions':
        actionType = 'Link';
        activity = 'Redeem promotion code';
        break;
    }

    if (!activity) {
      return;
    }

    this.trackingService.trackActivity(`Activity clicked:[${actionType}] ${activity}`, true);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: Window }): void {
    if (event.target.innerWidth > MOBILE_VIEW_TRESHOLD) {
      this.mobileView = false;
    } else {
      this.mobileView = true;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
